import { Loader2 } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// ── Outside-click hook ────────────────────────────────────────────────────────
const useOutsideClick = (callback) => {
  const ref = useRef();
  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) callback();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [callback]);
  return ref;
};

const LocationCompanySelector = ({ selectedLocation,SelectedLocationobject,setSelectedLocationobject, setSelectedLocation, selectedCompany, setSelectedCompany ,setSelectedCompanyname}) => {

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const locationDropdownRef = useOutsideClick(() => { if (isLocationOpen) setIsLocationOpen(false); });
  const companyDropdownRef = useOutsideClick(() => { if (isCompanyOpen) setIsCompanyOpen(false); });

  // ── Fetch locations on mount ──────────────────────────────────────────────────
  // Response: { success: true, locations: ["Gurugram", "Bhiwadi", ...] }
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (response.data.success && Array.isArray(response.data.locations)) {
          setLocations(response.data.locations);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, [BASE_URL]);

  // ── Fetch companies when location changes ─────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedLocation) { setCompanies([]); return; }
      try {
        setLoadingCompanies(true);
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(
          `${BASE_URL}/onDemandCompanies/list-companies/${SelectedLocationobject._id}`,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (response.data && Array.isArray(response.data.companies)) {
          setCompanies(response.data.companies);
        } else {
          setCompanies([]);
        }
      } catch {
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, [selectedLocation, BASE_URL]);

  // Reset company when location changes
  useEffect(() => { setSelectedCompany("");
    setSelectedCompanyname("")
   }, [selectedLocation]);

  return (
    <div className="mb-6 bg-white shadow-sm rounded-lg p-4">
      <div className="flex gap-4">

        {/* Location */}
        <div className="flex-1 relative" ref={locationDropdownRef}>
          <label className="text-sm text-gray-600 mb-1 block">
            Location <span className="text-red-500">*</span>
          </label>
          <button
            onClick={() => setIsLocationOpen(!isLocationOpen)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white hover:border-indigo-400 transition-colors"
          >
            <span className={selectedLocation ? "text-gray-800" : "text-gray-400"}>
              {selectedLocation || "Select Location"}
            </span>
            <svg
              className={`h-4 w-4 text-gray-400 transform transition-transform ${isLocationOpen ? "rotate-180" : ""}`}
              xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          {isLocationOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
              {locations.length > 0 ? (
                locations.map((location) => (
                  <button
                    key={location._id}
                    onClick={() => { setSelectedLocation(location.name);
                      setSelectedLocationobject(location);
                      setIsLocationOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === location.name ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                  >
                    {location.name}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No locations found</div>
              )}
            </div>
          )}
        </div>

        {/* Company */}
        <div className="flex-1 relative" ref={companyDropdownRef}>
          <label className="text-sm text-gray-600 mb-1 block">
            Company <span className="text-red-500">*</span>
          </label>
          <button
            onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen); }}
            disabled={!selectedLocation || loadingCompanies}
            className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center transition-colors
              ${!selectedLocation || loadingCompanies
                ? "bg-gray-100 cursor-not-allowed text-gray-400"
                : "bg-white hover:border-indigo-400"
              }`}
          >
            {loadingCompanies ? (
              <span className="flex items-center text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin mr-2" />Loading...
              </span>
            ) : (
              <span className={selectedCompany ? "text-gray-800" : "text-gray-400"}>
                {selectedCompany
  ? (() => {
      const c = companies.find((c) => c._id === selectedCompany);
      return c ? `${c.name}${c.companyCode ? ` (${c.companyCode})` : ""}` : "Select Company";
    })()
  : "Select Company"}
              </span>
            )}
            <svg
              className={`h-4 w-4 text-gray-400 transform transition-transform ${isCompanyOpen ? "rotate-180" : ""}`}
              xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          {isCompanyOpen && selectedLocation && !loadingCompanies && (
            <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
              {companies.length > 0 ? (
               companies.map((company) => (
  <button
    key={company._id}
    onClick={() => { setSelectedCompany(company._id); setSelectedCompanyname(company.name); setIsCompanyOpen(false); }}
    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
  >
    {company.name}{company.companyCode ? ` (${company.companyCode})` : ""}
  </button>
))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default LocationCompanySelector;